import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';
import InfiniteScroll from 'react-infinite-scroller';
import { useTribeClient } from '@tribeplatform/react-sdk';
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl';
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons';
import { Modal } from '@tribeplatform/react-ui-kit/Modal';
import { useDebounce } from '../common/hooks/useDebounce';
import { getGifUrl, Giphy, GiphyLogo } from '../common/lib/giphy';
import { linkToFile } from '../common/lib/upload';
import { T, useI18n } from '../i18n';
const GiphySvgLogo = loadable(() => import('../Composer/extensions/slash-menu/icons'), {
    resolveComponent: m => m.Giphy,
    ssr: false,
});
const Limit = 12;
const InitialOffset = 0;
const Preview = ({ gifs, onClick, }) => (_jsx("div", { className: "grid gap-3 grid-cols-2 sm:grid-cols-3", children: gifs.map(gif => (_jsx("div", { className: "col-span-1 aspect aspect-video overflow-hidden flex items-center justify-center", onClick: () => onClick(gif), children: _jsx("img", { src: getGifUrl(gif), alt: gif.title, className: "object-cover cursor-pointer" }, void 0) }, gif.id))) }, void 0));
export const GiphyPickerModal = ({ onUpload, open, close }) => {
    var _a, _b, _c, _d, _e;
    const intl = useI18n();
    const { config } = useTribeClient();
    const apiKey = config.giphyApiKey;
    const inputRef = useRef();
    const darkMode = false; // TODO: // read color from the theme provider when its available within the react-ui-kit
    const [query, setQuery] = useState();
    const [loading, setLoading] = useState(false);
    const [gifs, setGifs] = useState();
    const [trendingGifs, setTrendingGifs] = useState();
    const offset = useRef(InitialOffset);
    const scrollParentRef = useRef();
    const debouncedQuery = useDebounce(query, 300);
    const GiphyInstance = Giphy(apiKey);
    const resetState = () => {
        setQuery(undefined);
        offset.current = InitialOffset;
        setLoading(false);
        setGifs(undefined);
    };
    const onClose = () => {
        close();
        setTimeout(() => {
            resetState();
        }, 100);
    };
    const onClick = async (gif) => {
        const file = await linkToFile(getGifUrl(gif), intl);
        if (file) {
            onUpload(file);
            resetState();
        }
    };
    const search = useCallback(async (query, offset) => {
        setLoading(true);
        const payload = await GiphyInstance.search(query, {
            limit: Limit,
            offset,
        });
        if (offset) {
            setGifs({
                ...payload,
                data: [...gifs.data, ...payload.data],
            });
        }
        else {
            setGifs(payload);
        }
        setLoading(false);
    }, [gifs === null || gifs === void 0 ? void 0 : gifs.data]);
    const loadMore = () => {
        const newOffset = offset.current + Limit;
        search(debouncedQuery, newOffset);
        offset.current = newOffset;
    };
    useEffect(() => {
        GiphyInstance.trending({
            limit: Limit,
        })
            .then(result => {
            setTrendingGifs(result);
        })
            .catch(console.error);
    }, []);
    useEffect(() => {
        if (debouncedQuery) {
            search(debouncedQuery);
        }
    }, [debouncedQuery]);
    const hasMore = ((_a = gifs === null || gifs === void 0 ? void 0 : gifs.pagination) === null || _a === void 0 ? void 0 : _a.total_count) > ((_b = gifs === null || gifs === void 0 ? void 0 : gifs.pagination) === null || _b === void 0 ? void 0 : _b.count);
    if (!apiKey) {
        return null;
    }
    return (_jsxs(Modal, { open: open, onClose: onClose, className: "inline-flex flex-col sm:m-0 overflow-auto", initialFocus: inputRef, children: [_jsx(Modal.Header, { title: (_jsxs("span", { className: "flex items-center", children: [_jsx("span", { className: "flex items-center w-7 h-7 me-2", children: _jsx(GiphySvgLogo, {}, void 0) }, void 0), "Giphy"] }, void 0)) }, void 0), _jsxs(Modal.Content, { className: "w-full max-w-2xl mx-auto flex flex-col space-y-5", children: [_jsx(FormControl.Input, { ref: inputRef, autoComplete: "off", name: "query", placeholder: "Search Giphy", value: query, onChange: e => setQuery(e.target.value) }, void 0), !gifs && ((_c = trendingGifs === null || trendingGifs === void 0 ? void 0 : trendingGifs.data) === null || _c === void 0 ? void 0 : _c.length) && (_jsx(Preview, { gifs: trendingGifs.data, onClick: onClick }, void 0)), ((_d = gifs === null || gifs === void 0 ? void 0 : gifs.data) === null || _d === void 0 ? void 0 : _d.length) > 0 && (_jsx("div", { className: "max-h-72 overflow-auto", ref: scrollParentRef, children: _jsx(InfiniteScroll, { pageStart: 0, loadMore: loadMore, hasMore: hasMore || loading, useWindow: false, getScrollParent: () => scrollParentRef.current, children: _jsx(Preview, { gifs: gifs.data, onClick: onClick }, void 0) }, void 0) }, void 0)), ((_e = gifs === null || gifs === void 0 ? void 0 : gifs.pagination) === null || _e === void 0 ? void 0 : _e.total_count) === 0 && (_jsx("div", { children: _jsx(T, { id: "Picker.GiphyPickerModal.NotResultsText", defaultMessage: "No result were found" }, void 0) }, void 0)), _jsxs("div", { className: "flex justify-between items-center", children: [_jsx("div", { className: "flex-1" }, void 0), _jsx("div", { className: "flex flex-1 justify-center h-3", children: loading && _jsx(SpinnerIcon, { className: "animate-spin" }, void 0) }, void 0), _jsx("div", { className: "flex flex-1 justify-end", children: _jsx("img", { className: "h-3", src: darkMode ? GiphyLogo.dark : GiphyLogo.light, alt: "giphy logo" }, void 0) }, void 0)] }, void 0)] }, void 0)] }, void 0));
};
//# sourceMappingURL=GiphyPickerModal.js.map